/**
 * Tiny safe markdown renderer for landing-page additional details / terms.
 *
 * Supports: headings (#, ##, ###), bold (**text**), italic (*text* or _text_),
 *   links ([text](https://url)), unordered lists (- item), ordered lists
 *   (1. item), blockquotes (> text), inline code (`code`), paragraphs, and
 *   line breaks.
 *
 * SECURITY: this renderer is used for owner-controlled content (rendered on
 * publicly visible landing pages). It escapes raw HTML and only emits a
 * fixed allow-list of tags. Links are forced to `rel="noopener noreferrer"
 * target="_blank"` and only http(s) / mailto / tel schemes are permitted.
 */

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const SAFE_URL_RE = /^(https?:\/\/|mailto:|tel:|\/)/i;

function inline(text) {
  let s = escapeHtml(text);
  // Inline code (must run before * processing)
  s = s.replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-gray-100 text-[90%]">$1</code>');
  // Bold
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // Italic (single underscore or asterisk)
  s = s.replace(/(^|\s)_([^_]+)_(?=\s|$|[.,!?;:])/g, "$1<em>$2</em>");
  s = s.replace(/(^|\s)\*([^*]+)\*(?=\s|$|[.,!?;:])/g, "$1<em>$2</em>");
  // Links — [label](url)
  s = s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label, url) => {
    if (!SAFE_URL_RE.test(url)) return label;
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="underline">${label}</a>`;
  });
  return s;
}

export function renderMarkdown(src) {
  if (!src || typeof src !== "string") return "";
  const lines = src.replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Blank line — flush
    if (/^\s*$/.test(line)) {
      i++;
      continue;
    }

    // Heading
    const h = line.match(/^(#{1,3})\s+(.*)$/);
    if (h) {
      const level = h[1].length;
      const tag = `h${level + 2}`; // h3, h4, h5 to avoid collision with h1/h2 outside
      out.push(`<${tag} class="font-semibold mt-3 mb-1">${inline(h[2])}</${tag}>`);
      i++;
      continue;
    }

    // Blockquote
    if (/^\s*>\s?/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        buf.push(lines[i].replace(/^\s*>\s?/, ""));
        i++;
      }
      out.push(`<blockquote class="border-l-4 border-gray-300 pl-3 italic text-gray-600 my-2">${inline(buf.join(" "))}</blockquote>`);
      continue;
    }

    // Unordered list
    if (/^\s*[-*]\s+/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        buf.push(`<li>${inline(lines[i].replace(/^\s*[-*]\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ul class="list-disc pl-5 my-2 space-y-1">${buf.join("")}</ul>`);
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const buf = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
        buf.push(`<li>${inline(lines[i].replace(/^\s*\d+\.\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ol class="list-decimal pl-5 my-2 space-y-1">${buf.join("")}</ol>`);
      continue;
    }

    // Paragraph (collect consecutive non-empty/non-special lines)
    const buf = [line];
    i++;
    while (i < lines.length && lines[i].trim() && !/^(#{1,3}\s|>\s?|[-*]\s|\d+\.\s)/.test(lines[i])) {
      buf.push(lines[i]);
      i++;
    }
    out.push(`<p class="my-2 leading-relaxed">${inline(buf.join(" "))}</p>`);
  }
  return out.join("\n");
}
